import { forwardRef } from "react";
import { cva } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import Tooltip from "./Tooltip";
import { FOCUS_RING, DISABLED } from "./tokens";

/**
 * Botão do sistema.
 *
 * As variantes cobrem os usos que já existiam espalhados pelo app. O caso
 * `navy` nasceu do "Calcular frete" do checkout, que usava um `bg-slate-900`
 * que não existia em nenhum outro lugar: agora é a cor estrutural da marca.
 */
const button = cva(
  cn(
    "inline-flex items-center justify-center gap-2 rounded-lg font-semibold whitespace-nowrap",
    "transition-colors select-none",
    FOCUS_RING,
    DISABLED,
  ),
  {
    variants: {
      variant: {
        primary: "bg-brand-600 text-white hover:bg-brand-700",
        secondary: "bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300",
        navy: "bg-navy text-white hover:bg-navy-hover",
        danger: "bg-red-600 text-white hover:bg-red-700",
        /* Ação destrutiva secundária: o peso visual fica com a borda, não com o preenchimento. */
        dangerSoft: "bg-white border border-red-200 text-red-600 hover:bg-red-50",
        ghost: "text-slate-600 hover:bg-slate-100 hover:text-slate-900",
        outline: "bg-white border border-brand-200 text-brand-700 hover:bg-brand-50",
        link: "text-brand-600 hover:underline underline-offset-2 px-0",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4 text-sm",
        lg: "h-11 px-5 text-sm",
        xl: "h-12 px-6 text-base",
        icon: "h-10 w-10 shrink-0",
        iconSm: "h-8 w-8 shrink-0",
      },
      block: { true: "w-full", false: "" },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
);

const Button = forwardRef(function Button(
  { variant, size, block, icon: Icon, iconRight, loading = false, className, children, type = "button", disabled, title, ...rest },
  ref,
) {
  const isIconOnly = size === "icon" || size === "iconSm";

  if (process.env.NODE_ENV === "development" && isIconOnly && !rest["aria-label"] && !title) {
    // Botão só com ícone não tem texto acessível: leitor de tela anuncia "botão".
    console.warn("[ui-kit] Button só com ícone precisa de aria-label ou title.");
  }

  const iconSize = size === "sm" || size === "iconSm" ? 14 : size === "xl" ? 20 : 16;

  // `title` vira BALÃO do sistema, e não o do navegador: os dois juntos
  // mostrariam a mesma frase duas vezes, uma delas um segundo depois. Sem o
  // atributo no DOM, o `title` que era o único texto acessível do botão viraria
  // silêncio — daí ele ser promovido a `aria-label` quando não há outro.
  const alvo = (
    <button
      ref={ref}
      type={type}
      data-ring=""
      disabled={disabled || loading}
      aria-label={rest["aria-label"] ?? (title || undefined)}
      className={cn(button({ variant, size, block }), className)}
      {...rest}
    >
      {loading ? <Loader2 size={iconSize} className="animate-spin shrink-0" aria-hidden />
        : Icon ? <Icon size={iconSize} className="shrink-0" aria-hidden /> : null}
      {children}
      {!loading && iconRight ? (() => {
        const R = iconRight;
        return <R size={iconSize} className="shrink-0" aria-hidden />;
      })() : null}
    </button>
  );

  return <Tooltip label={title}>{alvo}</Tooltip>;
});

export default Button;
